/** اليوم بتوقيت القاهرة بصيغة YYYY-MM-DD (نفس مرجع الخادم) */
export function cairoToday(now: Date = new Date()): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  return p;
}
/**
 * **يوم الشغل** بتوقيت القاهرة — يقفل الساعة 4 الفجر لا 12 بالليل (0159، والمراجعة الشاملة 02/10/2026): المتابعة المسجّلة
 * 00:40 تُحفظ على اليوم السابق، فـ«النهاردة» في شاشات المتابعات هو هذا اليوم — وإلّا اختفى ما سُجّل بعد نص الليل من «اليوم».
 */
export function cairoWorkDay(now: Date = new Date()): string {
  return cairoToday(new Date(now.getTime() - 4 * 3600 * 1000));
}
/**
 * رقم الهاتف كما يُقرأ في مصر: `01019455272` لا `+201019455272`.
 *
 * التخزين قد يكون بأي صيغة (محلية كما أدخلها الأدمن، أو دولية `+20`) — والعرض واحد:
 * الرقم المصري بصفره المحلي. وما ليس مصريًّا يبقى كما هو بمقدّمته الدولية، فلا يُقصّ
 * رقم بلدٍ آخر ليبدو محليًّا. والفراغ ليس رقمًا: يعود «—» ولا يُخترع بديل.
 */
export function localPhone(raw: string | null | undefined): string {
  const compact = (raw ?? '').replace(/[\s()‏‎-]/g, '');
  if (!compact) return '—';
  // +20 / 0020 / 20 ثم رقم محمول وطني (1 ثم تسعة أرقام) ← صفر محلي
  const eg = /^(?:\+20|0020|20)(1\d{9})$/.exec(compact);
  return eg ? `0${eg[1]}` : compact;
}

/** الوقت الحالي بتوقيت القاهرة بصيغة HH:MM */
export function cairoNowTime(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Cairo', hour: '2-digit', minute: '2-digit', hour12: false }).format(now);
}

/**
 * لحظة واحدة مقروءة بتوقيت القاهرة: التاريخ `DD/MM/YYYY` والساعة `hh:MM:SS AM/PM` من
 * **نفس القراءة**. وحدة القراءة مقصودة: لو حُسب كلٌّ منهما على حدة لأمكن أن يُقرأ التاريخ
 * قبل منتصف الليل والساعة بعده بأجزاء من الثانية، فيظهر سطران من يومين مختلفين.
 * والتاريخ بترتيب يوم/شهر/سنة كبقيّة الشاشات (`shortDate`).
 *
 * **الساعة بنظام ١٢ ساعة مع AM/PM** (قرار المالك). وتُشتقّ الفترة من ساعة الـ٢٤ بالحساب
 * لا من نصّ `dayPeriod` الذي تُخرجه المكتبة: بعض المحرّكات تكتبه «am» أو «a.m.» أو بصيغة
 * ضيّقة، فيختلف الظاهر من متصفّح لآخر. والحساب واحد في كل مكان: منتصف الليل 12 AM،
 * والظهر 12 PM.
 */
export function cairoClock(now: Date = new Date()): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, hourCycle: 'h23',
  }).formatToParts(now);
  const at = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  // بعض المحرّكات تُخرج «24» لمنتصف الليل رغم h23 — والساعة صفر لا أربع وعشرون
  const h24 = at('hour') === '24' ? 0 : Number(at('hour'));
  const period = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return {
    date: `${at('day')}/${at('month')}/${at('year')}`,
    time: `${String(h12).padStart(2, '0')}:${at('minute')}:${at('second')} ${period}`,
  };
}
/** عرض مختصر للتاريخ: 12/09 */
/**
 * تاريخ للعرض: **يوم/شهر/سنة** — 2026-10-01 ← 01/10/2026، أي الأول من أكتوبر لا العاشر من يناير.
 * الترتيب مقصود وموحَّد في كل الشاشات حتى لا يلتبس اليوم بالشهر. القيمة المخزَّنة تبقى ISO
 * كما هي في القاعدة وفي كل نداءات الـAPI؛ هذا تنسيق عرض لا تحويل بيانات.
 */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
}
/**
 * **لحظة التسجيل بساعة القاهرة** (قرار المالك 01/10/2026: «عاوز أضيف مع التاريخ الوقت كمان علشان أعرف المتابعة دي
 * اتسجّلت إمتى وآخر متابعة البياع عملها كانت إمتى»): اليوم «يوم/شهر/سنة» والساعة «4:58 م» من طابعٍ زمنيّ ISO.
 */
export function cairoStamp(iso: string | null | undefined): { date: string; time: string; day: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d);
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
  const pm = get('dayPeriod').toLowerCase().startsWith('p');
  return {
    date: `${get('day')}/${get('month')}/${get('year')}`,
    day: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${Number(get('hour'))}:${get('minute')} ${pm ? 'م' : 'ص'}`,
  };
}
export function shortTime(t: string | null | undefined): string {
  return t ? t.slice(0, 5) : '—';
}
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
// ---------- المال (القاعدة المعتمدة: جنيه مصري، أرقام صحيحة بلا كسور) ----------
//
// الخادم يرسل المبالغ نصًّا عشريًا ("15000.00") لأن التخزين الداخلي بدقة 1/10000 جنيه —
// وهذه الدقة تبقى كما هي للمحاسبة. ما يتغيّر هو ما **يراه** المستخدم: جنيه مصري بأرقام
// صحيحة وفواصل آلاف، بلا `.00`.
//
// وقاعدة صريحة: **لا تقريب صامت.** إن حمل المبلغ كسرًا غير صفري — وهو ما تمنعه حقول
// الإدخال من الآن — يُعرض الكسر كما هو بدل إخفائه. إخفاء نصف جنيه أسوأ من إظهاره.

/** وحدة العملة كما تُكتب للمستخدم. */
/** رمز العملة كما يكتبه المالك في أوراقه: «ج.م» لا «جنيه مصري» — أقصر ويُقرأ بنظرة. */
/**
 * **الأرقام العربية تُقرأ أرقامًا** (قرار المالك 22/09/2026: «ليه غير صحيح؟ خلّيه يدخل
 * عربي أو إنجليزي براحته، وحوّله أنت»).
 *
 * لوحةُ المفاتيح العربية على الهاتف تكتب ١٠٠ لا 100، و`Number('١٠٠')` = NaN — فيُردّ
 * «مبلغ غير صحيح» على مبلغٍ صحيحٍ تمامًا. وتُقبل هنا الصيغتان: الهندية العربية (٠-٩)
 * والفارسية (۰-۹)، ومعهما الفاصلة العشرية العربية (٫) وفاصلة الآلاف (٬ و،).
 *
 * والتحويل **عرضٌ وقراءة لا تخزين**: ما يُحفظ في القاعدة أرقامٌ لاتينية كما كان دائمًا.
 */
export function toEnglishDigits(v: string): string {
  return v
    .replace(/[٠-٩۰-۹]/g, d => {
      const c = d.codePointAt(0) ?? 0;
      const zero = c >= 0x06F0 ? 0x06F0 : 0x0660;   // ۰ الفارسية · ٠ العربية
      return String(c - zero);
    })
    .replace(/٫/g, '.')       // الفاصلة العشرية العربية
    .replace(/[٬،]/g, '');    // فاصلة الآلاف — تُحذف لا تُترجم

}

export const EGP = 'ج.م';

const GROUP = new Intl.NumberFormat('en-US', { useGrouping: true, maximumFractionDigits: 0 });

/**
 * المبلغ كرقم معروض بلا وحدة: "15,000" — للخلايا التي يحمل عنوان عمودها العملة.
 *
 * **بالجنيه الصحيح دائمًا، مقرَّبًا لأقربه** (قرار المالك 22/09/2026: «ممكن تشيل
 * التقريب نهائي من البرنامج كله وتخلّي الأعداد كلها صحيحة فقط، مقرّبة لأقرب رقم
 * صحيح»). وكان الكسر يُعرض كما هو حيثما وُجد.
 *
 * والقروشُ لا تُدخَل أصلًا — الإدخال بالجنيه الصحيح (انظر isWholeEGP) — وإنّما
 * تنشأ من حساب: نسبةُ عمولة، وحصّةُ شريك، وكسرُ يومٍ في خصم. فهي أثرُ قسمةٍ لا مبلغٌ
 * قصده أحد، وعرضُها يطيل كل رقمٍ في كل شاشةٍ بلا أن يغيّر قرارًا.
 *
 * **ومَن احتاج القرشَ فله egpExact** — ويُستعمل حيث يكون القرشُ هو المقصود نفسه
 * (كقولنا «والرقم بالضبط»)، لا في كل مكان.
 */
export function egpNum(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  const r = Math.round(n);
  // ‎-0.4‎ تُقرَّب إلى ‎-0‎، و«‎-0‎» رقمٌ لا يُكتب في ورقة
  return GROUP.format(Object.is(r, -0) ? 0 : r);
}

/**
 * المبلغ بقرشه حين يكون القرشُ هو المقصود — لا يُستعمل إلّا حيث يُقال «بالضبط».
 * والكسرُ الصفري يُحذف: "15,000" لا "15,000.00".
 */
export function egpExact(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  const whole = Math.trunc(n);
  const frac = Math.abs(n - whole);
  if (frac < 1e-9) return GROUP.format(whole);
  return `${GROUP.format(whole)}.${String(Math.round(frac * 100)).padStart(2, '0')}`;
}

/** المبلغ كاملًا بوحدته: "15,000 ج.م" — للبطاقات والأسطر المستقلة. */
export function egp(v: string | number | null | undefined): string {
  const n = egpNum(v);
  return n === '—' ? n : `${n} ${EGP}`;
}

/**
 * هل المبلغ المكتوب جنيهات صحيحة؟
 *
 * تقبل ما يكتبه المستخدم ("10000") وما يرسله الخادم لحجز قائم ("10000.00") — فكلاهما نفس
 * المبلغ بلا كسر. وترفض الكسر الحقيقي ("10000.50") لأن القاعدة المعتمدة أن الإدخال بالجنيه
 * الصحيح. ولولا قبول الصيغة العشرية الصفرية لصار فتح حجز قديم وحفظه مستحيلًا.
 */
export function isWholeEGP(v: string): boolean {
  const t = v.trim();
  if (!/^\d{1,12}(\.\d+)?$/.test(t)) return false;
  return Number.isInteger(Number(t));
}

/** صيغة الإدخال: المبلغ بلا كسر صفري — "10000.00" يصير "10000" في الحقل. */
export const toWholeInput = (v: string | null | undefined): string => {
  const t = (v ?? '').trim();
  return isWholeEGP(t) ? String(Math.trunc(Number(t))) : t;
};

/** رقم الهاتف للعرض: بلا تغيير في التخزين (الخادم يحوّله إلى صيغة دولية) */
/**
 * عرض الهاتف كما يكتبه الموظف ويقرؤه: **بالشكل المصري المحلي** 01033720296.
 *
 * التخزين يبقى E.164 (+201033720296) كما هو في القاعدة وفي كل نداءات الـAPI — فالتطبيع
 * التقني ضرورة (بحث موحّد، ومفتاح فريد للعميل)، لكنه لا يجوز أن يتسرّب إلى عين المستخدم.
 * فالتحويل هنا **عرضٌ فقط**، ولا يُرسَل هذا الشكل إلى الخادم ولا يُخزَّن.
 *
 * وما ليس مصريًا يبقى كما هو: لا نخترع صيغة محلية لبلد لا نعرف قواعده.
 */
export const displayPhone = (p: string | null | undefined): string => {
  if (!p) return '—';
  const s = p.trim();
  const eg = /^\+20(1\d{9})$/.exec(s);
  if (eg) return `0${eg[1]}`;
  // غير المصري: 00 ثم المفتاح والرقم (قرار المالك 01/10/2026: «مش عاوز أي رقم يظهر بـ+ … لو سعودي يبقى 00966 وبعدها يكمل»)
  const intl = /^\+(\d{7,15})$/.exec(s);
  return intl ? `00${intl[1]}` : s;
};

// ---------- وقت الحضور بتوقيت القاهرة (Phase 5 — Step 7) ----------

/**
 * أجزاء اللحظة المطلقة بتوقيت القاهرة: التاريخ والساعة (0–23) والدقيقة.
 * الطابع يأتي من الخادم دائمًا (occurredAt)؛ هذه الدالة تعرضه ولا تخترعه.
 */
export function cairoParts(at: string | Date): { date: string; hour: number; minute: number } {
  const d = typeof at === 'string' ? new Date(at) : at;
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const at_ = (t: string): string => p.find(x => x.type === t)?.value ?? '00';
  // بعض المحرّكات تُخرج «24» لمنتصف الليل تحت hour12:false — تُطبَّع إلى 0
  return { date: `${at_('year')}-${at_('month')}-${at_('day')}`, hour: Number(at_('hour')) % 24, minute: Number(at_('minute')) };
}

/**
 * عرض 12 ساعة بالعربية — النطاقات المعتمدة حرفيًا وبتوقيت القاهرة:
 *   00:00–04:59 بعد منتصف الليل · 05:00–11:59 صباحًا · 12:00–12:59 ظهرًا · 13:00–23:59 مساءً
 * الكلمة تُشتق حسابيًا من ساعة القاهرة لا من Intl: صيغة ar تعطي «ص/م» وهي ليست القاعدة المعتمدة.
 * الأرقام لاتينية كبقية التطبيق (tabular-nums).
 */
export function dayPeriodAr(hour: number): string {
  return hour <= 4 ? 'بعد منتصف الليل' : hour <= 11 ? 'صباحًا' : hour === 12 ? 'ظهرًا' : 'مساءً';
}
export function cairoTimeAr(at: string | Date | null | undefined): string {
  if (!at) return '—';
  const { hour, minute } = cairoParts(at);
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(minute).padStart(2, '0')} ${dayPeriodAr(hour)}`;
}
/**
 * ساعة جدولٍ مكتوبة كما يقرؤها الموظف: `10:00:00` ← «10:00 صباحًا»، و`22:00` ← «10:00 مساءً».
 * والمدخل نصٌّ من القاعدة (HH:MM[:SS]) لا لحظةٌ مطلقة — فلا تحويل منطقة زمنية هنا.
 */
export function clockAr(t: string | null | undefined): string {
  if (!t) return '—';
  const m = /^(\d{1,2}):(\d{2})/.exec(t.trim());
  if (!m) return t;
  const hour = Number(m[1]);
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${m[2]} ${dayPeriodAr(hour)}`;
}

/** التاريخ والوقت معًا: 12/09/2026 · 5:30 صباحًا */
/** التاريخ وحده بتوقيت القاهرة: dd/mm/yyyy — لطابعٍ زمني كامل. */
export function cairoDateAr(at: string | Date | null | undefined): string {
  if (!at) return '—';
  return shortDate(cairoParts(at).date);
}

export function cairoDateTimeAr(at: string | Date | null | undefined): string {
  if (!at) return '—';
  return `${shortDate(cairoParts(at).date)} · ${cairoTimeAr(at)}`;
}

/** مدة بالدقائق كنصّ عربي: «7 ساعات و30 دقيقة». 0 يُعرض صفرًا صريحًا لا شرطة. */
export function durationAr(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  const hp = h === 0 ? '' : h === 1 ? 'ساعة' : h === 2 ? 'ساعتان' : h <= 10 ? `${h} ساعات` : `${h} ساعة`;
  const mp = m === 0 ? '' : m === 1 ? 'دقيقة' : m === 2 ? 'دقيقتان' : m <= 10 ? `${m} دقائق` : `${m} دقيقة`;
  if (!hp && !mp) return '0 دقيقة';
  return hp && mp ? `${hp} و${mp}` : (hp || mp);
}

/**
 * عنوان شهرٍ بالعربية ومعه صيغته الرقمية: «سبتمبر 2026 · 9/2026» (قرار المالك).
 *
 * الاسم يُقرأ بلمحة، والرقم يُطابَق به على الشاشات والتقارير الأخرى — فلا يُترك المستخدم
 * يحوّل `2026-09` في رأسه. ويُعاد النصّ كما جاء إن لم يكن شهرًا صالحًا.
 */
const MONTHS_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
                   'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

export function monthTitle(month: string | null | undefined): string {
  if (!month) return '—';
  const [y, m] = String(month).split('-');
  const i = Number(m) - 1;
  if (!y || Number.isNaN(i) || i < 0 || i > 11) return String(month);
  return `${MONTHS_AR[i]} ${y} · ${Number(m)}/${y}`;
}

/**
 * **ساعة القاهرة ⇐ لحظة ISO** (المراجعة الشاملة 02/10/2026): «تسجيل بصمة نيابةً عن الموظف» كان يقرأ اليوم والساعة بمنطقة
 * جهاز الأدمن (`new Date('…T…')`) — فجهازٌ على غير توقيت القاهرة يسجّل البصمة بساعةٍ أخرى. الآن تُقرأ بساعة القاهرة دائمًا،
 * بالتوقيت الصيفي والشتوي معًا (Intl يعرف فرق القاهرة في ذلك اليوم).
 */
export function cairoWallToIso(day: string, time: string): string {
  const [y, mo, d] = day.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const asUtc = Date.UTC(y, mo - 1, d, h, mi);
  const offsetAt = (ms: number) => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone: 'Africa/Cairo', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(new Date(ms)).map(x => [x.type, x.value]));
    return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute)) - ms;
  };
  // تقريبٌ ثم تصحيح — يكفي مرّتان حتى عند لحظة تغيير التوقيت
  let ms = asUtc - offsetAt(asUtc);
  ms = asUtc - offsetAt(ms);
  return new Date(ms).toISOString();
}
